<template>
  <div class="login-page">
    <!-- Left panel -->
    <div class="login-left">
      <AuthLeftPanel />
    </div>

    <!-- Right panel -->
    <div class="login-right">
      <div class="login-box">
        <!-- Step 1: Email + registration number -->
        <b-form v-if="step === 1" @submit.prevent="submitReset">
          <h5 class="form-title text-heading">{{ $t("forgot_password.title") }}</h5>
          <p class="form-subtitle text-body-sm">
            {{ $t("forgot_password.description") }}
          </p>

          <UITextField
            v-model="email"
            type="email"
            :label="$t('forgot_password.email_placeholder')"
          />
          <label class="text-label">{{ $t("forgot_password.otp_channel_label") }}</label>
          <div class="otp-channel-options">
            <label
              v-for="option in channelOptions"
              :key="option.value"
              class="otp-channel-option"
            >
              <input
                type="radio"
                name="otpChannel"
                :value="option.value"
                v-model="otpChannel"
              />
              {{ option.label }}
            </label>
          </div>
          <b-alert :show="isLocked" variant="danger" class="mt-2">
            {{ $t("forgot_password.locked") }}
            <div class="mt-1">
              <strong>{{ $t("login.locked_countdown", { time: lockCountdown }) }}</strong>
            </div>
          </b-alert>
          <b-alert :show="!!errorMsg" variant="danger" class="mt-2">{{
            errorMsg
          }}</b-alert>
          <UIButton
            type="submit"
            :label="isResendWaiting ? `${$t('forgot_password.submit')} (${resendCountdown})` : $t('forgot_password.submit')"
            variant="primary"
            :block="true"
            :loading="loading"
            :disabled="isLocked || isResendWaiting"
            class="mt-3"
          />
        </b-form>

        <!-- Step 2: OTP -->
        <template v-if="step === 2">
          <p v-if="otpChannel === 'email'" class="otp-hint">
            {{ $t("forgot_password.otp_hint_email", { email: maskedEmail }) }}
          </p>
          <p v-else class="otp-hint">
            {{ $t("forgot_password.otp_hint", { mobile: maskedMobile }) }}
          </p>
          <b-form @submit.prevent="submitOtp">
            <UITextField
              v-model="otp"
              type="text"
              :label="$t('forgot_password.otp_placeholder')"
            />
            <b-alert :show="!!errorMsg" variant="danger" class="mt-2">{{
              errorMsg
            }}</b-alert>
            <UIButton
              type="submit"
              :label="$t('forgot_password.otp_submit')"
              variant="primary"
              :block="true"
              :loading="loading"
              class="mt-3"
            />
          </b-form>
          <button class="resend-btn" :disabled="loading || isResendWaiting" @click="submitReset">
            {{ $t("forgot_password.resend") }}<template v-if="isResendWaiting"> ({{ resendCountdown }})</template>
          </button>
        </template>

        <!-- Step 3: New password -->
        <template v-if="step === 3">
          <b-form @submit.prevent="submitNewPassword">
            <label class="text-label">{{ $t("forgot_password.password_label") }}</label>
            <UITextField
              v-model="newPassword"
              type="password"
              autocomplete="new-password"
              :label="$t('forgot_password.password_placeholder')"
            />
            <label class="text-label">{{ $t("forgot_password.password_confirm_label") }}</label>
            <UITextField
              v-model="newPasswordConfirm"
              type="password"
              autocomplete="new-password"
              :label="$t('forgot_password.password_placeholder')"
            />
            <ul class="password-requirements">
              <li
                v-for="req in passwordRequirements"
                :key="req.key"
                :class="req.met ? 'requirement-met' : 'requirement-unmet'"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                {{ req.label }}
              </li>
            </ul>
            <b-alert :show="!!errorMsg" variant="danger" class="mt-2">{{
              errorMsg
            }}</b-alert>
            <UIButton
              type="submit"
              :label="$t('forgot_password.password_submit')"
              variant="primary"
              :block="true"
              :loading="loading"
              class="mt-3"
            />
          </b-form>
        </template>

        <div class="login-divider text-body-xs">
          <span>{{ $t("login.or") || "эсвэл" }}</span>
        </div>
        <UIButton
          :label="$t('forgot_password.back_to_login')"
          variant="secondary"
          :block="true"
          @click="$router.push('/user/login')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import UIButton from "../../components/UI/UIButton.vue";
import AuthLeftPanel from "../../components/UI/AuthLeftPanel.vue";
import UITextField from "../../components/UI/UITextField.vue";
import lockCountdownMixin from "../../mixins/lockCountdownMixin";
import { startCountdown, formatMmSs } from "../../utils/countdown";

// Backend-ийн otp.resendSecond-тэй тохирно
const DEFAULT_RESEND_SECONDS = 120;

function maskEmail(email) {
  const at = email.lastIndexOf("@");
  if (at < 1) return email;
  const local = email.slice(0, at);
  const domain = email.slice(at);
  if (local.length <= 2) return local[0] + "*".repeat(local.length - 1) + domain;
  const keep = local.length > 4 ? 2 : 1;
  return local.slice(0, keep) + "*".repeat(local.length - keep - 1) + local.slice(-1) + domain;
}

export default {
  name: "ForgotPasswordPage",
  components: { UIButton, AuthLeftPanel, UITextField },
  mixins: [lockCountdownMixin],
  data() {
    return {
      step: 1,
      otpChannel: "email",
      // Нэвтрэх хуудаснаас түгжигдсэн и-мэйлтэй ирж болно
      email: this.$route.query.email || "",
      otp: "",
      newPassword: "",
      newPasswordConfirm: "",
      maskedMobile: "",
      maskedEmail: "",
      resetToken: null,
      confirmToken: null,
      loading: false,
      errorMsg: "",
      resendSeconds: 0,
      stopResendTimer: null,
    };
  },
  computed: {
    isResendWaiting() {
      return this.resendSeconds > 0;
    },
    resendCountdown() {
      return formatMmSs(this.resendSeconds);
    },
    channelOptions() {
      return [
        { label: this.$t("forgot_password.otp_channel_phone"), value: "phone" },
        { label: this.$t("forgot_password.otp_channel_email"), value: "email" },
      ];
    },
    passwordRequirements() {
      const pwd = this.newPassword;
      const hasUpper = /[A-Z]/.test(pwd);
      const hasLower = /[a-z]/.test(pwd);
      const hasNumber = /[0-9]/.test(pwd);
      const hasSpecial = /[^A-Za-z0-9]/.test(pwd);
      return [
        { key: "max", label: this.$t("forgot_password.req_max"), met: pwd.length > 0 && pwd.length <= 15 },
        { key: "min", label: this.$t("forgot_password.req_min"), met: pwd.length >= 8 },
        {
          key: "case_number",
          label: this.$t("forgot_password.req_case_number"),
          met: hasUpper && hasLower && hasNumber,
        },
        { key: "special", label: this.$t("forgot_password.req_special"), met: hasSpecial },
      ];
    },
  },
  watch: {
    // Түгжээ нь и-мэйл тус бүрд хамаарна
    email() {
      this.stopLockCountdown();
      this.stopResendCountdown();
    },
  },
  beforeDestroy() {
    this.stopResendCountdown();
  },
  methods: {
    startResendCountdown(seconds) {
      this.stopResendCountdown();
      this.stopResendTimer = startCountdown(seconds, (left) => {
        this.resendSeconds = left;
      });
    },
    stopResendCountdown() {
      if (this.stopResendTimer) this.stopResendTimer();
      this.stopResendTimer = null;
      this.resendSeconds = 0;
    },
    async submitReset() {
      if (this.isLocked || this.isResendWaiting) return;
      this.errorMsg = "";
      this.loading = true;
      try {
        const { data } = await axios.post(
          "/api/enduser/forgot-password",
          {
            reference: this.email.trim(),
            by_email: this.otpChannel === "email",
          },
          { noAuth: true },
        );
        if (data && data.access_token) {
          this.resetToken = data.access_token;
          this.maskedMobile = data.mobile || "";
          this.maskedEmail = data.masked_email || maskEmail(this.email.trim());
          this.otp = "";
          this.step = 2;
          this.startResendCountdown(data.resend_seconds ?? DEFAULT_RESEND_SECONDS);
        } else {
          this.errorMsg = this.$t("forgot_password.error_generic");
        }
      } catch (err) {
        const data = err?.response?.data;
        // Саяхан код авсан: хүлээх хугацааг товчин дээр харуулна
        if (data?.code === "OTP_COOLDOWN") {
          this.errorMsg = this.$t("forgot_password.otp_cooldown");
          this.startResendCountdown(data.seconds);
          return;
        }
        if (err?.response?.status === 429 && data?.seconds > 0) {
          this.startLockCountdown(data.seconds);
          return;
        }
        this.errorMsg = data?.message || this.$t("forgot_password.error_generic");
      } finally {
        this.loading = false;
      }
    },
    async submitOtp() {
      this.errorMsg = "";
      this.loading = true;
      try {
        const { data } = await axios.post(
          "/api/enduser/forgot-password/confirm",
          {
            otp: this.otp.trim(),
            reference: this.email.trim(),
          },
          {
            headers: { Authorization: `Bearer ${this.resetToken}` },
            noAuth: true,
          },
        );
        if (data && data.access_token) {
          this.confirmToken = data.access_token;
          this.newPassword = "";
          this.newPasswordConfirm = "";
          this.step = 3;
        } else {
          this.errorMsg = this.$t("forgot_password.otp_error");
        }
      } catch (err) {
        this.errorMsg =
          err?.response?.data?.message || this.$t("forgot_password.otp_error");
      } finally {
        this.loading = false;
      }
    },
    async submitNewPassword() {
      this.errorMsg = "";
      if (this.newPassword !== this.newPasswordConfirm) {
        this.errorMsg = this.$t("forgot_password.password_mismatch");
        return;
      }
      if (!this.passwordRequirements.every((req) => req.met)) {
        this.errorMsg = this.$t("forgot_password.password_complexity");
        return;
      }
      this.loading = true;
      try {
        await axios.post(
          "/api/enduser/reset-password",
          {
            new_password: this.newPassword,
            repeat_password: this.newPasswordConfirm,
          },
          {
            headers: { Authorization: `Bearer ${this.confirmToken}` },
            noAuth: true,
          },
        );
        this.$router.push("/user/login?reset=success");
      } catch (err) {
        const data = err?.response?.data;
        if (data?.code === "NEW_PASSWORD_SAME_AS_OLD") {
          this.errorMsg = this.$t("forgot_password.password_same_as_old");
        } else {
          this.errorMsg = data?.message || this.$t("forgot_password.error_generic");
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ── Password requirements ── */
.password-requirements {
  list-style: none;
  margin: 0 0 8px;
  padding: 0;
}

.password-requirements li {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  padding: 4px 0;
}

.password-requirements li svg {
  flex-shrink: 0;
}

.requirement-met {
  color: #2f9e44;
}

.requirement-unmet {
  color: #e03131;
}

/* ── Layout ── */
.login-page {
  min-height: 100vh;
  display: flex;
}

.login-left {
  flex: 0 0 50%;
  overflow: hidden;
}

.login-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  padding: 10%;
  padding-top: 100px;
  overflow-y: auto;
  overflow-x: hidden;
}

@media (max-width: 768px) {
  .login-left {
    display: none;
  }
  .login-right {
    width: 100%;
  }
}

.login-box {
  width: 100%;
  max-width: 350px;
}

/* ── Titles ── */
.form-title {
  color: #1a2035;
  margin-bottom: 6px;
}
.form-subtitle {
  color: #71717a !important;
  margin-bottom: 24px;
  line-height: 1.5;
}

label {
  color: #555e6d;
  font-size: 13px;
  margin-bottom: 4px;
  display: block;
}

.otp-channel-options {
  display: flex;
  gap: 20px;
  margin-bottom: 16px;
}

.otp-channel-option {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #555e6d;
  cursor: pointer;
  margin-bottom: 0;
}

.otp-channel-option input[type="radio"] {
  margin: 0;
  cursor: pointer;
}

.otp-hint {
  color: #555e6d;
  font-size: 13px;
  margin-bottom: 16px;
  text-align: center;
}

.resend-btn {
  display: block;
  width: 100%;
  margin-top: 8px;
  background: none;
  border: none;
  color: #aab0bc;
  font-size: 12px;
  cursor: pointer;
  text-align: center;
  padding: 6px 0;
}
.resend-btn:hover {
  color: #2d3f9c;
}

/* ── Divider ── */
.login-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 20px 0;
  color: #aab0bc !important;
}
.login-divider::before,
.login-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #e4e7ef;
}
</style>
